// This file is automatically generated. Do not edit it directly.
import { createClient } from "@supabase/supabase-js";
import type { Database } from "./types";
import { stripUnexpectedGrant } from "@/utils/authUtils";
import { readSupabaseOverride } from "@/lib/chatbotBaseUrl";

// The branch switcher's override, if any. readSupabaseOverride enforces the dev flag, the
// stored shape, and the host allowlist — this URL becomes the auth client, so it is handed
// the user's password at sign-in and must never be an arbitrary host out of localStorage.
const override = readSupabaseOverride();
const SUPABASE_URL: string = override?.url ?? import.meta.env.VITE_SUPABASE_URL;
const SUPABASE_ANON_KEY: string = override?.anonKey ?? import.meta.env.VITE_SUPABASE_ANON_KEY;

// Worth a warning, not a log: the app is signed in against a preview branch rather than
// the project the build was configured with. The default case says nothing.
if (override) {
  console.warn("🔀 Using branch override:", override.branchName, "→", SUPABASE_URL);
}

// Validate hardcoded values (keeping validation for consistency)
if (!SUPABASE_URL) {
  throw new Error("VITE_SUPABASE_URL is required but missing.");
}

if (!SUPABASE_ANON_KEY) {
  throw new Error("VITE_SUPABASE_ANON_KEY is required but missing.");
}

// Import the supabase client like this:
// import { supabase } from "@/integrations/supabase/client";

/**
 * This page load's URL fragment, captured BEFORE createClient() below.
 *
 * On the implicit flow supabase-js consumes the fragment while it initialises and then
 * clears it (`window.location.hash = ''`), so anything reading `window.location.hash`
 * afterwards races that clearing. Reading it here — the first thing that runs in the
 * module that constructs the client — is the only way to see it reliably.
 *
 * /auth/callback needs it to tell a session THIS OAuth round trip minted from one gotrue
 * merely recovered from storage. Keep it above createClient().
 */
const initialUrlHash = typeof window !== "undefined" ? window.location.hash : "";

/** See initialUrlHash. */
export const readInitialUrlHash = () => initialUrlHash;

/**
 * Drop a grant handed to a page that is not supposed to receive one, before gotrue can read it.
 *
 * See stripUnexpectedGrant: `_initialize()` adopts an implicit grant off ANY route, so the
 * round-trip state on /auth/callback guards one path and leaves every other one open. This has
 * to run after the snapshot above (so the callback still sees its own fragment) and before
 * createClient(), which is the point gotrue starts reading the URL.
 */
if (typeof window !== "undefined") {
  const stripped = stripUnexpectedGrant(window.location.href);
  if (stripped) window.history.replaceState(window.history.state, "", stripped);
}

/**
 * Whether /auth/callback has already accepted the grant that fragment carried.
 *
 * The snapshot above outlives the mount that consumed it. `_getSessionFromURL` clears the hash
 * by assigning `window.location.hash = ''`, which pushes a fresh same-document history entry,
 * so the callback entry keeps its fragment: a browser Back after a successful sign-in restores
 * it and re-renders the route, with the round-trip state already spent. Without this latch the
 * page reads that stale grant as one nobody asked for and signs the user out.
 *
 * Scoped to the page load, deliberately — the same reason initialUrlHash is. A marker in
 * sessionStorage would survive into the next navigation in this tab, where it would wave
 * through an attacker's `/auth/callback#access_token=…` link unchecked.
 */
let initialGrantAccepted = false;

/** See initialGrantAccepted. */
export const markInitialGrantAccepted = () => {
  initialGrantAccepted = true;
};

/** See initialGrantAccepted. */
export const initialGrantWasAccepted = () => initialGrantAccepted;

let supabaseClient;

// Helper to check if localStorage is fully available (has required methods)
const isLocalStorageAvailable =
  typeof localStorage !== "undefined" && typeof localStorage.getItem === "function";

try {
  supabaseClient = createClient<Database>(SUPABASE_URL, SUPABASE_ANON_KEY, {
    auth: {
      persistSession: true,
      autoRefreshToken: true,
      storage: isLocalStorageAvailable ? localStorage : undefined,
    },
  });

  console.log("✅ Supabase client initialized successfully (SANDBOX MODE)");
} catch (error) {
  console.error("❌ Failed to initialize Supabase client:", error);
  // Create a mock client that will throw readable errors if used
  supabaseClient = new Proxy(
    {},
    {
      get: function (target, prop) {
        if (prop === "auth" || prop === "from" || prop === "storage") {
          return new Proxy(
            {},
            {
              get: function () {
                throw new Error(
                  "Supabase client not properly initialized. Check your configuration."
                );
              },
            }
          );
        }
        throw new Error("Supabase client not properly initialized. Check your configuration.");
      },
    }
  ) as any;
}

export const supabase = supabaseClient;
